import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { ChevronDownIcon, ChevronUpIcon, useMinimalsTheme } from '@tom-ui/ui'
import { slugify } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { ReactNode } from 'react'

interface Props {
  isSelected: boolean
  title?: string | null
  subtitle?: string | null
  hasLeftPaddingOnTitle?: boolean
  hasDetails?: boolean
  rightSummaryRender: ReactNode
  children: ReactNode
  onChange: () => void
}

export const ControlRoomAccordion = observer(
  ({
    isSelected,
    title,
    subtitle,
    hasLeftPaddingOnTitle,
    hasDetails,
    rightSummaryRender,
    children,
    onChange,
  }: Props) => {
    const theme = useMinimalsTheme()
    const { t } = useTranslate()

    const dataCySelector = `carrier-accordion-${slugify(title ?? 'unknown')}`

    const renderChevron = () => {
      if (!hasDetails) return null

      if (isSelected) {
        return <ChevronUpIcon />
      }
      return <ChevronDownIcon />
    }

    return (
      <Accordion
        data-cy={dataCySelector}
        expanded={isSelected}
        onChange={onChange}
        sx={{ margin: '0 !important', boxShadow: 'none !important' }}
      >
        <AccordionSummary
          data-cy={`${dataCySelector}-summary`}
          sx={{
            borderLeft: isSelected
              ? `${theme.customSpacing.xxs} solid ${theme.palette.primary.main}`
              : 'none',
            margin: 0,
            '& .MuiAccordionSummary-content': {
              margin: `${theme.customSpacing.xs} !important`,
            },
            backgroundColor: isSelected ? theme.palette.grey[200] : theme.palette.grey[100],
          }}
        >
          <Stack
            flexDirection='row'
            justifyContent='space-between'
            width='100%'
            alignItems='center'
            gap={theme.customSpacing.xxs}
            paddingLeft={hasLeftPaddingOnTitle ? theme.customSpacing.s : undefined}
          >
            <Stack flexDirection='row' alignItems='center' gap={theme.customSpacing.xxs}>
              {renderChevron()}

              <Stack>
                <Typography variant='subtitle2'>{title ?? t('unknown', 'Unknown')}</Typography>
                {subtitle && (
                  <Typography variant='body2'>{subtitle ?? t('unknown', 'Unknown')}</Typography>
                )}
              </Stack>
            </Stack>

            <Stack flexDirection='row' alignItems='center' gap={theme.customSpacing.xxs}>
              {rightSummaryRender}
            </Stack>
          </Stack>
        </AccordionSummary>
        {hasDetails && <AccordionDetails sx={{ padding: 0 }}>{children}</AccordionDetails>}
      </Accordion>
    )
  },
)
